export type AiProvider =
  | 'elevenlabs'
  | 'openai'
  | 'mistral'
  | 'france-travail'
  | 'gladia'
  | 'gradium';

export type AiProviderStatus = 'operational' | 'degraded' | 'down';

export type StatusPageIndicator = 'none' | 'minor' | 'major' | 'critical';

/** Per-provider latency thresholds for the "degraded" signal (ms). */
export const AI_PROVIDER_LATENCY_LIMITS_MS: Record<AiProvider, number> = {
  elevenlabs: 2_000,
  openai: 4_000,
  mistral: 4_000,
  'france-travail': 1_500,
  gladia: 2_000,
  gradium: 2_000,
};

/** Status page components we actually consume per provider. */
export const AI_PROVIDER_WATCHED_COMPONENTS: Partial<Record<AiProvider, string[]>> = {
  elevenlabs: ['Text to Speech', 'Conversations', 'Quality', 'Speech to Text'],
  openai: ['Chat Completions', 'Responses'],
  mistral: ['Mistral', 'Mistral Data Website'],
  gradium: ['Realtime API - Europe', 'Realtime API - US East', 'Voice Cloning'],
};

export interface AiProviderMeasured {
  latencyStatus: AiProviderStatus;
  errorStatus: AiProviderStatus;
  latencyP50Ms: number | null;
  latencyP95Ms: number | null;
  errorRatePercent: number;
  sampleCount: number;
  /** ISO 8601 timestamp of the last recorded call (real or probe). */
  lastCallAt: string | null;
}

export interface AiProviderDeclared {
  indicator: StatusPageIndicator;
  description: string;
  /** Worst status derived from watched components only. */
  status: AiProviderStatus;
  /** Names of affected watched components (empty if none affected). */
  affectedComponents: string[];
}

export interface AiProviderHealth {
  provider: AiProvider;
  /** Worst-of (latencyStatus, errorStatus, declared.status). */
  status: AiProviderStatus;
  measured: AiProviderMeasured;
  declared: AiProviderDeclared | null; // null: no public status page
}

export interface AiProvidersHealthResponse {
  windowMinutes: number;
  providers: AiProviderHealth[];
}

export type AiIncidentSeverity = 'error' | 'warn';
export type AiIncidentCategory = 'parsing' | 'fallback' | 'disconnect' | 'timeout';

export interface AiIncident {
  id: string;
  timestamp: string;
  severity: AiIncidentSeverity;
  category: AiIncidentCategory;
  provider?: AiProvider | string;
  title: string;
  details?: string;
  sessionOrInterviewId?: string;
  boothId?: string;
  metadata?: Record<string, unknown>;
}

export interface AiQualityMetrics {
  truncationRate: number;
  avgReasoningTokens: number;
  avgCacheHitRate: number;
  avgLatencyMs: number;
  totalRequests: number;
  judgeScorePercent?: number;
}

export interface AiObservabilityResponse {
  providersHealth: AiProvidersHealthResponse;
  incidents: AiIncident[];
  qualityMetrics: AiQualityMetrics;
}
